import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { InboxMessagePopup } from 'features/inboxMessages/components/InboxMessagePopup';
import { usePopupMessage } from 'features/inboxMessages/hooks/usePopupMessage';
import { Route, Switch } from 'react-router-dom';
import { IncidentsMap } from 'components/IncidentsMap';
import { useMapDrawerEntityForPoiState } from 'state/useMapDrawerEntityForPoiState';
import {
  MapEntityDrawer,
  MAP_ENTITY_DRAWER_MIN_SNAP_PCT,
  MAP_ENTITY_DRAWER_MAX_SNAP_PCT,
  CAMERA_DRAWER_MIN_SNAP_PCT,
} from 'features/mapping/components/MapEntityDrawer';
import { CameraDrawerDetails } from 'features/cameras/components/CameraDrawerDetails';
import { MapProvider } from 'shared/map-exports';
import { useWaterLevelEnabled } from 'features/waterLevel/hooks/useWaterLevelEnabled';
import { GeoEventDetails } from 'pages/GeoEventDetails';
import { MapDrawerEntityContentForPoi } from 'features/mapping/components/MapDrawerEntityContentForPoi';
import { MapPinDrawerContent } from 'features/mapping/components/MapPinDrawerContent';
import { PlaceDrawerContent } from 'features/places/components/PlaceDrawerContent';
import { parseLatLngFromUrl, useIsMapPinActive } from 'features/mapping/mapPin';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { Dialog } from '@mui/material';
import { Report } from 'app/pages/Report';
import { EditReport } from 'app/pages/EditReport';
import { WeatherStationDetails } from 'pages/WeatherStationDetails';
import { HoursDrawer } from 'features/locations/components/HoursDrawer';
import { useAQIStationsLayerEnabled } from 'features/mapping/hooks/useAQIStationsLayerEnabled';
import { AQIDetails } from 'pages/AQIDetails';
import { WaterLevelDetails } from 'pages/WaterLevelDetails';
import { AlertDetails } from 'pages/AlertDetails';
import { ExternalGeoEventDetails } from 'pages/ExternalGeoEventDetails';
import { EditReportActionMenu } from 'features/reports/components/GeoEventReports/EditReportActionMenu/EditReportActionMenu';
import { LocationDetails } from '../LocationContent/LocationDetails';
import {
  EXTERNAL_GEO_EVENT_DETAILS_ROUTE_PATH,
  INCIDENT_EDIT_REPORT_ROUTE_PATH,
  ROUTER_ENTITY_TO_ROUTE_PATH_MAP,
  INCIDENT_VIEW_REPORT_ROUTE_PATH,
  MAP_COORDINATES_ROUTE_PATHS,
  MAP_PIN_ROUTE_PATHS,
} from '../../../../constants';

const MAP_DRAWER_ENTITY_SNAP_POINTS = [
  MAP_ENTITY_DRAWER_MIN_SNAP_PCT,
  0.47,
  MAP_ENTITY_DRAWER_MAX_SNAP_PCT,
];

const CAMERA_MAP_DRAWER_ENTITY_SNAP_POINTS = [
  CAMERA_DRAWER_MIN_SNAP_PCT,
  0.4,
  0.7,
  MAP_ENTITY_DRAWER_MAX_SNAP_PCT,
];

export const HomeContent = (): JSX.Element => {
  const { t } = useTranslation();
  const { message, setMessageAsRead } = usePopupMessage();
  const { mapDrawerEntityForPoi, setMapDrawerEntityForPoi } =
    useMapDrawerEntityForPoiState();

  const showWaterLevel = useWaterLevelEnabled();
  const isMapPinActive = useIsMapPinActive();
  const pinImprovements = useReleaseFlag('pin_drop_improvements');

  // We still need useIsMapPinActive() check for internal/pro users when the flag is off
  const renderNewMapPinRoute = isMapPinActive || pinImprovements;

  // With the flag on, a pin lives at its own `/pin` route (plain coordinates are
  // the URL-synced center); the legacy behavior treats coordinates as the pin.
  const pinRoutePaths = pinImprovements
    ? MAP_PIN_ROUTE_PATHS
    : MAP_COORDINATES_ROUTE_PATHS;

  const showAQISensors = useAQIStationsLayerEnabled();

  return (
    <>
      <Helmet>
        {Capacitor.isNativePlatform() ? (
          <title>{t('home.title')}</title>
        ) : (
          <title>{t('home.webAppTitle')}</title>
        )}
        <meta property="og:title" content="Watch Duty Wildfire Map" />
        <meta
          property="og:description"
          content="Wildfire map and tracker with reports, wind, satellites, and more"
        />
      </Helmet>

      <MapProvider>
        <IncidentsMap />
      </MapProvider>

      <Switch>
        {renderNewMapPinRoute && (
          <Route
            exact
            path={pinRoutePaths}
            render={(props) => {
              const { latitudeParam, longitudeParam } = parseLatLngFromUrl(
                props.match.params,
              );
              return (
                <MapEntityDrawer
                  snapPointsPct={MAP_DRAWER_ENTITY_SNAP_POINTS}
                  defaultSnapPointPct={0.47}
                  open
                  entityId={`mapPin-${latitudeParam},${longitudeParam}`}
                >
                  <MapPinDrawerContent
                    lat={latitudeParam}
                    lng={longitudeParam}
                  />
                </MapEntityDrawer>
              );
            }}
          />
        )}
        {pinImprovements && (
          <Route
            exact
            path={['/place/:placeId']}
            render={(props) => (
              <MapEntityDrawer
                snapPointsPct={MAP_DRAWER_ENTITY_SNAP_POINTS}
                defaultSnapPointPct={0.47}
                open
                entityId={props.match.params.placeId}
              >
                <PlaceDrawerContent
                  placeId={Number(props.match.params.placeId)}
                />
              </MapEntityDrawer>
            )}
          />
        )}
        <Route
          exact
          path={[
            ROUTER_ENTITY_TO_ROUTE_PATH_MAP.wildfire,
            INCIDENT_VIEW_REPORT_ROUTE_PATH,
            INCIDENT_EDIT_REPORT_ROUTE_PATH,
          ]}
          render={(props) => (
            <>
              <MapEntityDrawer
                snapPointsPct={MAP_DRAWER_ENTITY_SNAP_POINTS}
                defaultSnapPointPct={0.47}
                open
                entityId={props.match.params.geoEventId}
              >
                <GeoEventDetails />
              </MapEntityDrawer>

              <Switch>
                <Route
                  exact
                  path={INCIDENT_VIEW_REPORT_ROUTE_PATH}
                  render={(routeProps) => (
                    <Dialog fullScreen open>
                      <Report {...routeProps} />
                    </Dialog>
                  )}
                />
                <Route
                  exact
                  path={INCIDENT_EDIT_REPORT_ROUTE_PATH}
                  render={(routeProps) => (
                    <Dialog fullScreen open>
                      <EditReport {...routeProps} />
                    </Dialog>
                  )}
                />
              </Switch>
            </>
          )}
        />
        <Route
          exact
          path={ROUTER_ENTITY_TO_ROUTE_PATH_MAP.location}
          render={(props) => (
            <>
              <MapEntityDrawer
                snapPointsPct={MAP_DRAWER_ENTITY_SNAP_POINTS}
                defaultSnapPointPct={0.47}
                open
                entityId={props.match.params.locationId}
              >
                <LocationDetails />
              </MapEntityDrawer>
              <HoursDrawer />
            </>
          )}
        />
        <Route
          exact
          path={ROUTER_ENTITY_TO_ROUTE_PATH_MAP.camera}
          render={(props) => (
            <MapEntityDrawer
              snapPointsPct={CAMERA_MAP_DRAWER_ENTITY_SNAP_POINTS}
              defaultSnapPointPct={0.7}
              open
              entityId={props.match.params.cameraId}
            >
              <CameraDrawerDetails />
            </MapEntityDrawer>
          )}
        />
        {showAQISensors && (
          <Route
            exact
            path={ROUTER_ENTITY_TO_ROUTE_PATH_MAP.aqi}
            render={(props) => (
              <MapEntityDrawer
                snapPointsPct={MAP_DRAWER_ENTITY_SNAP_POINTS}
                defaultSnapPointPct={0.47}
                open
                entityId={props.match.params.sensorId}
              >
                <AQIDetails />
              </MapEntityDrawer>
            )}
          />
        )}
        <Route
          exact
          path={ROUTER_ENTITY_TO_ROUTE_PATH_MAP.weatherStation}
          render={(props) => (
            <MapEntityDrawer
              snapPointsPct={MAP_DRAWER_ENTITY_SNAP_POINTS}
              defaultSnapPointPct={0.47}
              open
              entityId={props.match.params.stationId}
            >
              <WeatherStationDetails />
            </MapEntityDrawer>
          )}
        />
        {showWaterLevel && (
          <Route
            exact
            path={ROUTER_ENTITY_TO_ROUTE_PATH_MAP.gauge}
            render={(props) => (
              <MapEntityDrawer
                snapPointsPct={MAP_DRAWER_ENTITY_SNAP_POINTS}
                defaultSnapPointPct={0.47}
                open
                entityId={props.match.params.gaugeId}
              >
                <WaterLevelDetails />
              </MapEntityDrawer>
            )}
          />
        )}
        <Route
          exact
          path={EXTERNAL_GEO_EVENT_DETAILS_ROUTE_PATH}
          render={(props) => (
            <MapEntityDrawer
              snapPointsPct={MAP_DRAWER_ENTITY_SNAP_POINTS}
              defaultSnapPointPct={0.47}
              open
              entityId={props.match.params.externalGeoEventId}
            >
              <ExternalGeoEventDetails />
            </MapEntityDrawer>
          )}
        />
        <Route
          exact
          path={ROUTER_ENTITY_TO_ROUTE_PATH_MAP.alert}
          render={(props) => (
            <MapEntityDrawer
              snapPointsPct={MAP_DRAWER_ENTITY_SNAP_POINTS}
              defaultSnapPointPct={0.47}
              open
              entityId={props.match.params.alertId}
            >
              <AlertDetails />
            </MapEntityDrawer>
          )}
        />
        <Route
          exact
          path={['/', ...MAP_COORDINATES_ROUTE_PATHS]}
          // Keep <MapEntityDrawer /> mounted so the mobile drawer slides open
          // rather than popping open
          render={() => (
            <MapEntityDrawer
              snapPointsPct={MAP_DRAWER_ENTITY_SNAP_POINTS}
              defaultSnapPointPct={0.47}
              onClose={() => {
                if (mapDrawerEntityForPoi) {
                  setMapDrawerEntityForPoi(null);
                }
              }}
              open={Boolean(mapDrawerEntityForPoi)}
              entityId={mapDrawerEntityForPoi?.id}
            >
              <MapDrawerEntityContentForPoi />
            </MapEntityDrawer>
          )}
        />
      </Switch>

      {/* Sibling of MapEntityDrawer, not nested inside it: MapEntityDrawer's
          mobile sheet uses expandOnContentDrag, which would otherwise treat
          swipes on this sheet as drags on the drawer underneath it. */}
      <EditReportActionMenu />

      <InboxMessagePopup
        open={Boolean(message)}
        onClose={setMessageAsRead}
        message={message}
      />
    </>
  );
};
